import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { X, PaperPlaneRight, Sparkle } from '@phosphor-icons/react';
import AITaskPreview from '@/components/AITaskPreview';

const API = `${window.location.origin}/api`;

function QuickUpdateModal({ taskId, taskTitle, currentStatus, currentProject, onClose, onComplete }) {
  const [mode, setMode] = useState('manual'); // 'manual' or 'ai'
  const [note, setNote] = useState('');
  const [status, setStatus] = useState('');
  const [projectClient, setProjectClient] = useState('');
  const [details, setDetails] = useState('');
  const [showDetails, setShowDetails] = useState(false);
  const [sending, setSending] = useState(false);
  const [projects, setProjects] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [aiInput, setAiInput] = useState('');
  const [aiParsing, setAiParsing] = useState(false);
  const [aiPreview, setAiPreview] = useState(null);
  const noteRef = useRef(null);

  useEffect(() => {
    if (mode === 'manual') noteRef.current?.focus();
    axios.get(`${API}/tasks/projects`, { withCredentials: true })
      .then(res => setProjects(res.data))
      .catch(() => {});
  }, [mode]);

  const filteredSuggestions = projects.filter(
    p => p.toLowerCase().includes(projectClient.toLowerCase()) && p !== projectClient
  );

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;

    setSending(true);
    try {
      const body = { note: note.trim() };
      if (status) body.status = status;
      if (projectClient.trim()) body.project_client = projectClient.trim();
      if (details.trim()) body.details = details.trim();

      await axios.post(`${API}/tasks/${taskId}/quick-update`, body, { withCredentials: true });
      if (onComplete) onComplete();
    } catch (error) {
      console.error('Quick update failed:', error);
      alert('Failed to add update.');
    } finally {
      setSending(false);
    }
  };

  const handleAiParse = async () => {
    if (!aiInput.trim()) return;
    setAiParsing(true);
    try {
      const res = await axios.post(
        `${API}/ai/parse-task`,
        { text: aiInput, context: 'update_task', task_id: taskId },
        { withCredentials: true }
      );
      setAiPreview(res.data);
    } catch (error) {
      console.error('AI parse failed:', error);
      alert('AI parsing failed. Try manual entry.');
    } finally {
      setAiParsing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, y: 10, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-lg shadow-lg max-w-md w-full max-h-[90vh] overflow-y-auto"
        data-testid="quick-update-modal"
      >
        <div className="border-b border-slate-200 p-4 flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Quick Update</h2>
            <p className="text-xs text-slate-500 truncate max-w-[280px]">{taskTitle}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="close-quick-update">
            <X size={20} />
          </button>
        </div>

        {/* Mode tabs */}
        <div className="flex border-b border-slate-200">
          <button
            onClick={() => { setMode('manual'); setAiPreview(null); }}
            className={`flex-1 text-sm font-medium py-2.5 text-center transition-colors ${
              mode === 'manual' ? 'text-slate-900 border-b-2 border-slate-900' : 'text-slate-400 hover:text-slate-600'
            }`}
            data-testid="mode-manual"
          >
            Manual
          </button>
          <button
            onClick={() => setMode('ai')}
            className={`flex-1 text-sm font-medium py-2.5 text-center transition-colors flex items-center justify-center gap-1.5 ${
              mode === 'ai' ? 'text-navy border-b-2 border-blue-600' : 'text-slate-400 hover:text-slate-600'
            }`}
            data-testid="mode-ai"
          >
            <Sparkle size={14} weight="fill" /> AI Assist
          </button>
        </div>

        {mode === 'manual' ? (
          <form onSubmit={handleManualSubmit} className="p-4 space-y-3">
            <div>
              <input
                ref={noteRef}
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="What did you do?"
                required
                className="bg-white border border-slate-200 rounded-lg focus:border-slate-900 focus:ring-2 focus:ring-slate-100 px-4 py-2.5 w-full text-slate-900 text-sm"
                data-testid="quick-note-input"
              />
            </div>

            <div className="flex gap-2">
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 flex-1"
                data-testid="quick-status-select"
              >
                <option value="">Status (no change)</option>
                <option value="pending">Pending</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
              </select>

              <div className="relative flex-1">
                <input
                  type="text"
                  value={projectClient}
                  onChange={(e) => { setProjectClient(e.target.value); setShowSuggestions(true); }}
                  onFocus={() => setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                  placeholder="Project/Client"
                  className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 w-full"
                  data-testid="quick-project-input"
                />
                {showSuggestions && filteredSuggestions.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-md z-10 max-h-32 overflow-y-auto">
                    {filteredSuggestions.map((p) => (
                      <button
                        key={p}
                        type="button"
                        className="block w-full text-left px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                        onMouseDown={() => { setProjectClient(p); setShowSuggestions(false); }}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {!showDetails ? (
              <button type="button" onClick={() => setShowDetails(true)} className="text-xs text-slate-500 hover:text-slate-700" data-testid="show-details-btn">
                + Add details
              </button>
            ) : (
              <textarea
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                placeholder="Additional details..."
                className="bg-white border border-slate-200 rounded-lg px-4 py-2 w-full text-sm text-slate-700 min-h-[60px] resize-none"
                data-testid="quick-details-input"
              />
            )}

            <button
              type="submit"
              disabled={sending || !note.trim()}
              className="w-full bg-slate-900 text-white hover:bg-slate-800 transition-colors font-medium py-2.5 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
              data-testid="submit-quick-update"
            >
              <PaperPlaneRight size={14} weight="fill" />
              {sending ? 'Saving...' : 'Save Update'}
            </button>
          </form>
        ) : (
          <div className="p-4">
            {aiPreview ? (
              <AITaskPreview
                parsed={aiPreview.parsed}
                originalText={aiPreview.original_text}
                mode="update_task"
                taskId={taskId}
                projects={projects}
                onSave={() => { if (onComplete) onComplete(); }}
                onCancel={() => setAiPreview(null)}
              />
            ) : (
              <div className="space-y-3">
                <textarea
                  value={aiInput}
                  onChange={(e) => setAiInput(e.target.value)}
                  placeholder='e.g., "Met with Chidi at Zenith Bank today and agreed to send revised NDA by Thursday"'
                  className="bg-white border border-slate-200 rounded-lg focus:border-navy focus:ring-2 focus:ring-blue-100 px-4 py-3 w-full text-sm text-slate-900 min-h-[80px] resize-none"
                  data-testid="ai-update-input"
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAiParse(); } }}
                />
                <p className="text-xs text-slate-500">
                  AI will extract the update note, status changes, project/client, and due dates.
                </p>
                <button
                  onClick={handleAiParse}
                  disabled={aiParsing || !aiInput.trim()}
                  className="w-full bg-navy text-white hover:bg-navy-hover transition-colors font-medium py-2.5 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
                  data-testid="ai-parse-update-btn"
                >
                  <Sparkle size={14} weight="fill" />
                  {aiParsing ? 'Parsing...' : 'Parse with AI'}
                </button>
              </div>
            )}
          </div>
        )}
      </motion.div>
    </div>
  );
}

export default QuickUpdateModal;
